import React, { useCallback, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ChevronLeft, Plus, Pencil, Hash, CalendarRange, Wallet, User, PiggyBank, Download, ClipboardCheck, Sparkles, Globe, Star,
  Power, RotateCcw, Lock,
} from 'lucide-react';
import api from '../../../utils/api';
import { useConfirm } from '../../../components/ConfirmDialog';
import {
  PageHeader, Tabs, TabPanel, Button, Pill, RowMenu, Banner, Sheet, Skeleton, LoadingBlock, ErrorBlock,
  money, useLoad, errMsg, fmtDate, words, downloadFile,
} from '../kit';
import { useLogTime, useOnTimeSaved } from '../workflow/LogTimeProvider';
import SetupChecklist, { SetupBanner } from '../workflow/SetupChecklist';
import ScReferrals from '../workflow/ScReferrals';
import { ParticipantHours } from '../workflow/ScHours';
import NotesTab from './NotesTab';
import PlanTab from './PlanTab';
import CommsTab from './CommsTab';
import RecordsTab from './RecordsTab';
import ParticipantEmail from '../../Mail/ParticipantEmail';
import {
  SCModal, ServiceModal, StatementModal, PortalAccessModal, SurveyModal, ScReportModal, ClosureModal, EmailToNoteModal,
} from './modals';
import { Chip, LEVELS, fundingLabel, fundingType, fullName } from './shared';

// A participant's file: who they are and their plan in one compact header, setup as a single line
// until it is done, then the spec's sections as tabs. Log time is the one primary action.
const TABS = [
  { value: 'notes', label: 'Notes & Billing' },
  { value: 'plan', label: 'Plan & Budgets' },
  { value: 'referrals', label: 'Referrals' },
  { value: 'comms', label: 'Communications' },
  { value: 'email', label: 'Email' },
  { value: 'hours', label: 'SC Hours' },
  { value: 'records', label: 'Records' },
];
// Old links keep working: the Overview tab is now Notes & Billing (setup, tasks and key dates moved
// there), and the two record sections share the Records tab.
const TAB_OF = { notes: 'notes', overview: 'notes', plan: 'plan', referrals: 'referrals', comms: 'comms', email: 'email', hours: 'hours', records: 'records', compliance: 'records', documents: 'records' };

export default function ParticipantFile({ scId, staff = [], clients = [], perms = [] }) {
  const [sp, setSp] = useSearchParams();
  const rawTab = sp.get('tab');
  const tab = TAB_OF[rawTab] || 'notes';
  const setTab = (t) => setSp(t === 'notes' ? {} : { tab: t }, { replace: true });
  const confirm = useConfirm();
  const logTime = useLogTime();

  const d = useLoad(scId ? `/support-coordination/${scId}` : null);
  const setup = useLoad(scId ? `/sc/engagements/${scId}/checklist` : null);
  const forecast = useLoad(scId ? `/sc/claims/forecast/${scId}` : null);
  const { reload: reloadDetail } = d;
  const { reload: reloadSetup } = setup;
  const { reload: reloadForecast } = forecast;
  const refresh = useCallback(() => { reloadDetail(); reloadSetup(); reloadForecast(); }, [reloadDetail, reloadSetup, reloadForecast]);
  // Logged time changes what is left of the support coordination budget.
  useOnTimeSaved(reloadForecast);

  const [modal, setModal] = useState(null);   // 'edit' | 'statement' | 'portal' | 'survey' | 'report' | 'close' | 'email' | 'service'
  // The setup checklist opens from the address too (?setup=1), so the Claims Fix list can link
  // straight to it. Closing drops only that param; the tab and anything else stay.
  const setupOpen = sp.get('setup') === '1';
  const setSetupOpen = (on) => setSp((p) => { const n = new URLSearchParams(p); if (on) n.set('setup', '1'); else n.delete('setup'); return n; }, { replace: true });
  const [commsTick, setCommsTick] = useState(0);
  const close = () => setModal(null);

  const detail = d.data && String(d.data.id) === String(scId) ? d.data : null;
  const crumb = (
    <nav aria-label="Breadcrumb" className="mb-2">
      <Link to="/support-coordination?view=engagements"
        className="inline-flex items-center gap-1 rounded-lg -ml-1 px-1 py-1 text-[13px] font-medium text-slate-600 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
        <ChevronLeft size={15} aria-hidden />Caseload
      </Link>
    </nav>
  );

  if (d.error && !detail) {
    return (
      <div>
        <PageHeader before={crumb} title="Participant file" />
        <ErrorBlock error={/not found/i.test(d.error) ? 'This participant file doesn’t exist, or it belongs to another company.' : d.error} onRetry={reloadDetail} />
      </div>
    );
  }
  if (!detail) {
    return (
      <div aria-busy="true" aria-label="Loading the participant file">
        {crumb}
        <Skeleton className="h-8 w-64 mb-3" />
        <div className="flex flex-wrap gap-2 mb-6">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-7 w-32 rounded-full" />)}</div>
        <Skeleton className="h-9 w-full max-w-xl mb-5" />
        <LoadingBlock rows={6} />
      </div>
    );
  }

  const name = fullName(detail.client_first, detail.client_last) || detail.name || 'Participant';
  const coordinator = fullName(detail.coordinator_first, detail.coordinator_last);
  const ft = fundingType(detail.funding_source);
  const t = forecast.data && forecast.data.totals;
  const hasBudget = !!(t && Number(t.budget) > 0);
  const remaining = t ? Number(t.remaining) : 0;
  const lowPct = 10;
  const budgetTone = !hasBudget ? 'violet' : remaining < 0 ? 'red' : remaining < (Number(t.budget) * lowPct) / 100 ? 'amber' : 'violet';
  const closed = detail.status === 'closed';
  const canFixSetup = perms.includes('manage_referrals') || perms.includes('admin');
  const log = () => logTime({ coordinationId: detail.id, participantName: name, budgets: detail.budgets || [], services: detail.services || [] });

  const auditPack = async () => {
    const id = toast.loading('Building the audit pack…');
    try { await downloadFile(`/support-coordination/${detail.id}/audit-pack`, { filename: 'sc-audit-pack.zip' }); toast.success('Audit pack downloaded', { id }); }
    catch (e) { toast.error(errMsg(e, 'The audit pack wasn’t built. Try again.'), { id }); }
  };
  const reopen = async () => {
    if (!await confirm(`Reopen ${name}’s file? It becomes active again and time can be logged against it.`, { title: 'Reopen file', confirmLabel: 'Reopen' })) return;
    try { await api.patch(`/support-coordination/${detail.id}/reopen`); toast.success('File reopened'); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader
        before={crumb}
        title={name}
        sub={[LEVELS[detail.level] || (detail.level ? words(detail.level) : null), detail.disability].filter(Boolean).join(' · ') || undefined}
        actions={(
          <>
            <Button variant="secondary" icon={Pencil} onClick={() => setModal('edit')}>Edit</Button>
            <Button icon={Plus} onClick={log}>Log time</Button>
            <RowMenu label="More actions for this participant" items={[
              { label: 'Statement (PDF)', icon: Download, onClick: () => setModal('statement') },
              { label: 'Audit pack (ZIP)', icon: ClipboardCheck, onClick: auditPack },
              { label: 'Draft a report with AI', icon: Sparkles, onClick: () => setModal('report') },
              'sep',
              { label: 'Participant portal access', icon: Globe, onClick: () => setModal('portal') },
              { label: 'Ask for feedback', icon: Star, onClick: () => setModal('survey') },
              'sep',
              closed ? { label: 'Reopen file', icon: RotateCcw, onClick: reopen }
                : { label: 'Close file', icon: Power, danger: true, onClick: () => setModal('close') },
            ]} />
          </>
        )}>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Pill status={detail.status} />
          {!detail.is_active && !closed && <Pill tone="slate" label="Inactive" />}
          {detail.ndis_number && <Chip icon={Hash}>NDIS {detail.ndis_number}</Chip>}
          {(detail.plan_start || detail.plan_end) && <Chip icon={CalendarRange}>Plan {fmtDate(detail.plan_start)} – {fmtDate(detail.plan_end)}</Chip>}
          <Chip icon={Wallet} tone={ft ? 'slate' : 'amber'}>{fundingLabel(detail.funding_source)}</Chip>
          <Chip icon={User} label="Support coordinator" tone={coordinator ? 'slate' : 'amber'}>{coordinator || 'No coordinator'}</Chip>
          {hasBudget && (
            <button type="button" onClick={() => setTab('plan')} className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
              <Chip icon={PiggyBank} tone={budgetTone}>
                {remaining < 0 ? `SC budget over by ${money(-remaining)}` : `SC budget left ${money(remaining)}`}
              </Chip>
            </button>
          )}
        </div>
      </PageHeader>

      {closed && (
        <div className="mb-5">
          <Banner tone="violet" icon={Lock} title={`File closed${detail.closure_date ? ` on ${fmtDate(detail.closure_date)}` : ''}${detail.closure_reason ? `: ${words(detail.closure_reason).toLowerCase()}` : ''}`}>
            {detail.closure_outcome || 'Reopen it from the actions menu to log new time.'}
          </Banner>
        </div>
      )}
      <SetupBanner checklist={setup} onOpen={() => setSetupOpen(true)} />

      <Tabs value={tab} onChange={setTab} tabs={TABS} label="Section">
        <TabPanel value="notes">
          <NotesTab detail={detail} perms={perms} participantName={name} onChanged={refresh}
            onEditKeyDates={() => setModal('edit')} onFromEmail={() => setModal('email')} />
        </TabPanel>
        <TabPanel value="plan"><PlanTab detail={detail} onChanged={refresh} perms={perms} /></TabPanel>
        <TabPanel value="referrals">
          <ScReferrals embedded clientId={detail.client_id} coordinationId={detail.id} participantName={name} />
        </TabPanel>
        <TabPanel value="comms"><CommsTab detail={detail} participantName={name} refreshSignal={commsTick} /></TabPanel>
        <TabPanel value="email">
          <ParticipantEmail clientId={detail.client_id} name={name} to={detail.client_email ? [{ name, address: detail.client_email }] : []} />
        </TabPanel>
        <TabPanel value="hours">
          <ParticipantHours clientId={detail.client_id} coordinationId={detail.id} participantName={name} budgets={detail.budgets} services={detail.services} />
        </TabPanel>
        <TabPanel value="records">
          <RecordsTab scId={detail.id} participantName={name} initialSection={rawTab === 'documents' ? 'documents' : 'safety'} />
        </TabPanel>
      </Tabs>

      <Sheet open={setupOpen} onClose={() => setSetupOpen(false)} title="Finish setup" icon={ClipboardCheck} hue="amber"
        sub="Claims, invoices and SC hours need these before the first claim.">
        <SetupChecklist checklist={setup} coordinationId={detail.id} staff={staff} canEdit={canFixSetup} onChanged={refresh}
          onAddService={() => { setSetupOpen(false); setModal('service'); }} />
      </Sheet>

      {modal === 'edit' && <SCModal editing={detail} clients={clients} staff={staff} onClose={close} onSaved={() => { close(); refresh(); }} />}
      {modal === 'service' && <ServiceModal scId={detail.id} onClose={close} onSaved={() => { close(); refresh(); setSetupOpen(true); }} />}
      {modal === 'statement' && <StatementModal scId={detail.id} participantName={name} onClose={close} />}
      {modal === 'portal' && <PortalAccessModal scId={detail.id} participantName={name} onClose={close} />}
      {modal === 'survey' && (
        <SurveyModal scId={detail.id} modal={{ channel: detail.client_email ? 'email' : 'sms', to: detail.client_email || detail.client_phone || '' }}
          onClose={close} onSent={() => { close(); setCommsTick((n) => n + 1); }} />
      )}
      {modal === 'report' && <ScReportModal scId={detail.id} onClose={close} />}
      {modal === 'close' && <ClosureModal scId={detail.id} participantName={name} onClose={close} onClosed={() => { close(); refresh(); }} />}
      {modal === 'email' && (
        <EmailToNoteModal participantName={name} onClose={close}
          onDraft={(text) => { close(); logTime({ coordinationId: detail.id, participantName: name, budgets: detail.budgets || [], services: detail.services || [], defaults: { summary: text } }); }} />
      )}
    </div>
  );
}
